import { useId, useContext, useImperativeHandle, useState, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ChangeSetPicker from "../../changeSets/ChangeSetPicker";
import type { ChangeSetChoice, CommitBuilder } from "../../changeSets/changeSetChoice";
import { ChangeSetStepContext, type StepSave } from "./changeSetStep";
import StepSaveNotice from "./StepSaveNotice";
import type { DraftSaveHandle } from "./draftGuardContext";

interface AddToChangeSetProps {
  ref?: Ref<DraftSaveHandle>;
  projectId: string;
  /** The title a new change set gets unless the user changes it ("Add step: …"). */
  defaultTitle: string;
  /** The commits that save the draft, in order; read when the user adds it. */
  commits: () => readonly CommitBuilder[];
  /** A reason the draft cannot be added yet, such as a missing name; `null` if it can. */
  blockedBy: string | null;
  /** Called once the draft is saved: in a change set, or found to be what the latest
   * version already holds, so nothing opened. The draft is no longer unsaved either way. */
  onSaved?: () => void;
  /** Called with the change set once the draft is in it. */
  onAdded?: (changeSetId: string) => void | Promise<void>;
  /** What else the change set gets, said before saving (W-5's pin cascade). */
  notice?: ReactNode;
  /** A change set to suggest, such as the one a step made in the same session went into. */
  initialChangeSetId?: string | null;
}

/**
 * The end of every form (AU-1, AU-3): the draft goes into a new change set or one of the
 * user's open ones, for curators to review. Nothing is written to `main`. An edit saves
 * through its "Edit an item" step, which it answers; the step's Thread reviews the change
 * set (ADR-0031 D2).
 */
export default function AddToChangeSet({ ref, projectId, defaultTitle, commits, blockedBy, onSaved, onAdded, notice, initialChangeSetId = null }: AddToChangeSetProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const step = useContext(ChangeSetStepContext);
  const [choice, setChoice] = useState<ChangeSetChoice>(initialChangeSetId === null ? { kind: "new", title: defaultTitle } : { kind: "existing", changeSetId: initialChangeSetId });
  const [saved, setSaved] = useState<StepSave | null>(null);
  const submission = useSubmission();
  const sending = submission.state.status === "submitting";
  // A new change set keeps following the draft's name until the user types a title.
  const [titleEdited, setTitleEdited] = useState(false);
  const shownChoice = choice.kind === "new" && !titleEdited ? { kind: "new" as const, title: defaultTitle } : choice;

  const add = (): Promise<boolean> => {
    if (sending || blockedBy !== null || (shownChoice.kind === "new" && shownChoice.title.trim() === "")) return Promise.resolve(false);
    return new Promise((resolve) => {
      submission.submit(async () => {
        try {
          const outcome = await step.save(projectId, shownChoice, commits());
          setSaved(outcome);
          onSaved?.();
          if (outcome.kind !== "nothingToChange") {
            setChoice({ kind: "existing", changeSetId: outcome.changeSetId });
            await onAdded?.(outcome.changeSetId);
          }
          resolve(true);
        } catch (error) {
          resolve(false);
          throw error;
        }
      });
    });
  };
  useImperativeHandle(ref, () => ({ save: add }));

  return (
    <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-sm font-semibold">{t("authoring.changeSet.title")}</h3>
      <ChangeSetPicker
        projectId={projectId}
        choice={shownChoice}
        defaultTitle={defaultTitle}
        onChange={(next) => {
          setTitleEdited(next.kind === "new" && next.title !== defaultTitle);
          setChoice(next);
        }}
      />
      {notice}
      {blockedBy === null ? null : <p className="app-help-text text-xs">{blockedBy}</p>}
      {/* The full notice: a refused save is reported with its request's reference (J2-09); side lists keep the short one. */}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
      {saved !== null && submission.state.status === "succeeded" ? <StepSaveNotice saved={saved} shownIn={step.shownIn} /> : null}
      <AppButton variant="primary" size="sm" disabled={blockedBy !== null || sending || (shownChoice.kind === "new" && shownChoice.title.trim() === "")} onClick={() => { void add(); }}>
        {sending ? t("authoring.changeSet.adding") : t("authoring.changeSet.add")}
      </AppButton>
      <p className="app-help-text text-xs">{t("authoring.changeSet.hint")}</p>
    </section>
  );
}

